import {
  BillingLineSide,
  InvoiceCategoryFilter,
  LineIneligibilityReason,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isString, uniq } from 'lodash-es';

import { BillingLine } from '~hooks/useBillingLines/models';
import { TabQSParam } from '~pages/Approvals/DriverPay/TabQSParam';
import { ROUTES } from '~router/routePaths';
import { getLineIneligibilityReasonLabel } from '~utils/lineIneligibilityReasonLabel';

import { getLinesSettlementNumbers } from './groupBillingLines';

export const BILLING_LINE_SIDES = [BillingLineSide.PAYABLE, BillingLineSide.RECEIVABLE];

export const categoryBySide = {
  [BillingLineSide.PAYABLE]: InvoiceCategoryFilter.PAYABLES,
  [BillingLineSide.RECEIVABLE]: InvoiceCategoryFilter.RECEIVABLES,
} satisfies Record<BillingLineSide, InvoiceCategoryFilter>;

export function getSettlementsTabPath(
  category: InvoiceCategoryFilter,
  settlementName: string,
) {
  const query = new URLSearchParams({
    category,
    search: settlementName,
    tab: TabQSParam.SETTLEMENTS,
  });

  return `/${ROUTES.approvals.base}/${ROUTES.approvals.driverPay}?${query}`;
}

export function isBillingLineSelectable(
  line: BillingLine,
): line is BillingLine & { id: string } {
  return line.eligible && line.id !== null;
}

export const sideLabelKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.selection.side.payable',
  [BillingLineSide.RECEIVABLE]: 'approvals.approve_charges.selection.side.receivable',
} satisfies Record<BillingLineSide, string>;

export function getSettleStatusBySide(lines: BillingLine[]) {
  return BILLING_LINE_SIDES.flatMap((side) => {
    const sideLines = getLinesOfSide(lines, side);

    if (sideLines.length === 0) {
      return [];
    }

    const sideLabel = t(sideLabelKeys[side]);
    const blockedLines = sideLines.filter((line) => !isBillingLineSelectable(line));

    if (blockedLines.length === 0) {
      return [t('approvals.approve_charges.selection.side_ready', { side: sideLabel })];
    }

    const settlementNumbersLabel = getLinesSettlementNumbers(blockedLines).join(', ');
    const reasonsLabel =
      uniq(
        blockedLines
          .flatMap((line) => line.ineligibilityReasons)
          .map((reason) => {
            const isOnKnownSettlement =
              reason === LineIneligibilityReason.ALREADY_SETTLED &&
              Boolean(settlementNumbersLabel);

            return isOnKnownSettlement
              ? t('approvals.approve_charges.selection.on_settlement', {
                  numbers: settlementNumbersLabel,
                })
              : getLineIneligibilityReasonLabel(reason);
          }),
      ).join(', ') || t('approvals.approve_charges.ineligibility.unknown');
    const readyCount = sideLines.length - blockedLines.length;

    if (readyCount === 0) {
      return [
        t('approvals.approve_charges.selection.side_blocked', {
          reasons: reasonsLabel,
          side: sideLabel,
        }),
      ];
    }

    return [
      t('approvals.approve_charges.selection.side_partly_ready', {
        count: readyCount,
        reasons: reasonsLabel,
        side: sideLabel,
        total: sideLines.length,
      }),
    ];
  });
}

export function getLineIds(lines: BillingLine[]) {
  return lines.map((line) => line.id).filter(isString);
}

export function toSettlementLines(lines: BillingLine[]) {
  return lines.flatMap((line) =>
    line.id === null ? [] : [{ id: line.id, kind: line.kind }],
  );
}

export function getBillingLinesParties(lines: BillingLine[]) {
  const partyByKey = new Map(
    lines.map(({ scope: { party } }) => [`${party.type}:${party.id}`, party]),
  );

  return Array.from(partyByKey.values());
}

export function getLinesOfSide(lines: BillingLine[], side: BillingLineSide) {
  return lines.filter((line) => line.side === side);
}
